import React, { useEffect } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  CardContent,
  Modal,
  IconButton,
  TextField,
  Typography,
  Tooltip,
  Grid,
  Select,
  MenuItem,
  Alert, 
  AlertTitle, 
  AlertProps,
} from "@mui/material";
import { Theme, useTheme } from '@mui/material/styles'
import { createStyles, makeStyles } from '@mui/styles';
import { useLocalStorage } from "../../utils/LocalStorageHook";
import { downloadBankCSV, getSalesOffices } from "../../utils/GetAdmin";
import { uploadBankCSV,approveBankCSV } from "../../utils/PostAdmin"
import requestMissingContexts, { determineConfig } from "../../utils/GetContext";
import TableAbstraction from "../../componentUtils/TableAbstraction"
import {checkForRole} from "../../utils/GetAuth";
import {
  Lock,
  GetApp,
  ExpandMore,
  Attachment,
  Publish,
  Save,
  AddCircle,
  RemoveCircle,
  Help,
  Clear,
 } from '@mui/icons-material';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    centerDiv: {
      textAlign: "center",
    },
    accordion: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
    },
    root: {},
    divWidth: {
      width: "100%",
    },
    card: {
    },
    primary: {
      marginTop: theme.spacing(2),
    },
    gridSpaceable: {
      padding: theme.spacing(2),
    },
    gridMarginTop: {
      marginTop: theme.spacing(2),
    },
    paragraph: {
      padding: theme.spacing(1),
    },
    paragraphAct: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    paragraphButton: {
      marginLeft: theme.spacing(2),
      minWidth: 150,
    },
    input: {
      paddingLeft: "20px", 
      paddingRight: "20px", 
      width:"350px"
    },
    paper: {
        position: 'absolute',
        backgroundColor: theme.palette.background.paper,
        border: `1px solid`,
        borderColor: theme.palette.border.main, 
        boxShadow: theme.shadows[4],
        padding: theme.spacing(2, 2, 2),
    },
  })
);
function getModalStyle() {
  const top = 50;
  const left = 50;

  return {
      top: `${top}%`,
      left: `${left}%`,
      transform: `translate(-${top}%, -${left}%)`,
  };
}
export default function AdminBanks() {
  const classes = useStyles();
  const [salesOffices, setSalesOffices] = React.useState<any>();
  const [clientId,] = useLocalStorage("SETTINGS.clientId", "100");
  const [salesOrg,] = useLocalStorage("SETTINGS.salesOrg", "");
  const [doneLoading, setDoneLoading] = React.useState<boolean>();
  const [codes, setCodes] = React.useState<any>();
  
  const [modalStyle] = React.useState(getModalStyle);
  const [alertLevel, setAlertLevel] = React.useState<AlertProps>({severity:"info"});
  const [alertTitle, setAlertTitle] = React.useState<string>();
  const [alertMessage, setAlertMessage] = React.useState<string>();
  const [openAlertWindow, setOpenAlertWindow] = React.useState(false);
  const handleModalClose = () => {
    setOpenAlertWindow(false);
  }
  
  const enabled = checkForRole(clientId,salesOrg,'vp_admin_bulk');

  const [salesOffice, setSalesOffice] = React.useState<string>('*');
  const [selectedFile, setSelectedFile] = React.useState<File>();

  const [bankSummary, setBankSummary] = React.useState<{add:number, banks: number, remove: number, unchanged:number}>();
  const [bankADD, setBankADD] = React.useState<{salesOffice:string, bankID: string, bankName: string, customerNum:string, change:string}[]>();
  const [bankREMOVE, setBankREMOVE] = React.useState<{salesOffice:string, bankID: string, bankName: string, customerNum:string, change:string}[]>();
  const [bankKeyId, setBankKeyId] = React.useState<string>();
  const [bankMessage, setBankMessage] = React.useState<string>();
  const [bankErrorBody, setBankErrorBody] = React.useState<any>();
  
  useEffect(() => {
    if (doneLoading) return;
    requestMissingContexts(["SALESOFFICES"])
      .then((v) => {
        setCodes(v);
      })
      .then((_) => {
          getSalesOffices(salesOrg)
            .then((v) => {
              setSalesOffices(v);
              setDoneLoading(true);
            });
          });
  }, [salesOffices,salesOrg,selectedFile,doneLoading,bankKeyId,bankErrorBody]);
  return ( 
      enabled ? (
    <div className={classes.root} >
    <Typography variant="h4" className={classes.paragraph}>
        Admin Bulk Bank Maintenance
      </Typography>
      <Typography
        variant="subtitle1"
        className={classes.paragraphAct}
        paragraph
      >
        This page allows you to bulk maintain the banks in VendPrime and VendSmart. 
        <strong>Use this tool with caution as you may wipe out data.</strong> 
      </Typography>
      <Grid container spacing={2} className={classes.primary}>
        <Grid item xs={'auto'} md={'auto'} xl={'auto'}>
            <Accordion className={classes.accordion}  style={{maxWidth:window.innerWidth - 300}}>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <Typography variant="h5" className={classes.paragraph}>
                  <Help /> How to Use
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                
                <Card className={classes.card}>
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      className={classes.paragraphAct}
                      paragraph={true}
                      noWrap={false}
                    >
                      First download the file with all current banks. This can be done as either the whole sales org, or a single <strong>Sales Office</strong>. 
                      Be aware that there is no locking mechanism in the application to prevent changes from happening after the file is downloaded. 
                      Other users may maintain individual banks after you downloaded the file. 
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      className={classes.paragraphAct}
                      paragraph={true}
                      noWrap={false}
                    >
                      Make your modifications to the downloaded csv file. In the database, banks are keyed off the <strong>Bank ID</strong>. 
                      Banks can have same naming but cannot have same <strong>Bank ID</strong>. If you keep existing GUID when modifying the file,
                      the application will be able to detect actual changes (for example, if a customer was added to the bank). 
                      If you change the <strong>Bank ID</strong>, the delta report will treat it as a deletion of a bank, and the creation of a new one. 
                      The <strong>Customer Name</strong> field is provided in the export for information only, and is not required to be maintained when uploading the file back.
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      className={classes.paragraphAct}
                      paragraph={true}
                      noWrap={false}
                    >
                      After you are done making your changes, upload the file. Changes will be attempted for 
                      the current <strong>Sales Office</strong> selected in the dropdown.
                      You will be shown how much is changing and will be asked to confirm the action. 
                      Once you have confirmed, the changes will be applied to the application's database. 
                      At this point, the only way to restore the old data is to re upload the file you downloaded. 
                      Please keep both the old and new files in case the banks need to be restored to a previous state. 
                    </Typography>
                  </CardContent>
                </Card>
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={'auto'} md={'auto'} xl={'auto'}>
            <Card className={classes.card}>
              <CardContent>
              <Typography variant="h5" className={classes.paragraph}>
                Stage Changes
              </Typography>
              <Typography variant="subtitle1" className={classes.paragraph} paragraph>
                Sales Office: 
                <Select placeholder='salesOffice' value={salesOffice}
                    className={classes.input}
                    onChange={(e) => setSalesOffice(`${e.target.value}`)}>
                    <MenuItem key='*' value='*'>{`${salesOrg} - All`}</MenuItem>
                    {(salesOffices)?(Object.entries(salesOffices).map((so: any, i: number) => (
                        <MenuItem key={`${so[1].code}`} value={`${so[1].code}`}>
                          {`${so[1].code} - ${so[1].text}`}
                        </MenuItem>
                        ))):('')}
                </Select>
              </Typography>
                <div>
                  <Button
                    color="primary"
                    variant="contained"
                    className={classes.paragraphButton}
                    onClick={(event) => {
                        downloadBankCSV(salesOrg, salesOffice).then( reply => {
                          const url = window.URL.createObjectURL( new Blob([reply?.data], {type: 'text/csv'}));
                          const link = document.createElement('a');
                          link.href = url;
                          
                          const date = new Date();
                          const year = date.getFullYear();
                          const month = `${date.getMonth() + 1}`.padStart(2, '0');
                          const day =`${date.getDate()}`.padStart(2, '0');
                          
                          link.setAttribute('download', `BANK_${salesOrg}_${(salesOffice && salesOffice !== "*") ? salesOffice : 'ALL'}_${year}${month}${day}.csv`);
                          document.body.appendChild(link);
                          link.click();
                        })
                      }
                    }
                  >
                    <GetApp/> Download
                  </Button>
                </div>
                <br/>
                <div className={classes.divWidth}>
                    <Button
                      color="primary"
                      component="label"
                      variant="contained"
                      style={{marginRight:10}}
                      className={classes.paragraphButton}>
                      <Attachment style={{marginRight:3}} /> Select File
                      <input type="file" name="banks" id='bank-file' 
                        hidden
                        onChange={(e) => {
                          setSelectedFile(undefined)
                          if(e.target.files){
                            setSelectedFile(e.target.files[0]);
                          } 
                        }
                      }/>
                    </Button>
                    <TextField placeholder='File' type='text' value={selectedFile?.name} />
                  </div>
                  <br/>
                    <Button
                        color="primary"
                        variant="contained"
                        className={classes.paragraphButton}
                        disabled={(selectedFile)?false:true}          
                        onClick={(event) => {
                            if(selectedFile){
                              setBankSummary(undefined);
                              setBankADD(undefined);
                              setBankREMOVE(undefined);
                              setBankMessage(undefined);
                              setBankKeyId(undefined);
                              uploadBankCSV(salesOrg,salesOffice,selectedFile).then((result) => {
                                if(result && result.status < 400){
                                  if(result.data.bankDelta){
                                    setBankSummary(result.data.bankDelta.counts);
                                    setBankADD(result.data.bankDelta.ADD);
                                    setBankREMOVE(result.data.bankDelta.REMOVE);
                                    setBankKeyId(result.data.keyId);
                                    setBankErrorBody(undefined);
                                  }
                                } else {
                                  setBankErrorBody(result?.data || {error:"UNKNOWN ERROR"});
                                }
                                let message:string = result?.data?.message || 'An UNKNOWN ERROR occured! This may have been caused by modifying the file after selecting it. Please try renaming the file, then uploading the renamed file again. If this does not resolve, please reach out to CONA Services with an incident.'
                                setBankMessage(message);
                              })
                            }
                          }
                        }
                      >
                        <Publish/> Upload
                    </Button>
                </CardContent>
              </Card>
            </Grid>
                {
                  (bankMessage && !bankErrorBody)?( /* Upload Detected */
                    <Grid item xs={'auto'} md={'auto'} xl={'auto'}>
                      <Card className={classes.card}>
                        <CardContent>
                          <Typography variant="h5" className={classes.paragraph}>
                            Approve Changes
                          </Typography>
                          <Typography
                            variant="subtitle1"
                            className={classes.paragraphAct}
                            paragraph
                          >
                            {bankMessage}  <br/>
                            Total Banks: {bankSummary?.banks}
                          </Typography>
                          <Alert severity="warning"><AlertTitle>Customer Change Summary</AlertTitle>Added: {bankSummary?.add}<br/>Removed: {bankSummary?.remove}<br/>Unchanged: {bankSummary?.unchanged}</Alert>
                          <br/>
                          <Button
                              color="primary"
                              variant="contained"
                              className={classes.paragraphButton}
                              onClick={(event) => {
                                  if(bankKeyId){
                                    approveBankCSV(salesOrg, salesOffice, bankKeyId).then( reply => {
                                     // console.log(reply?.data?.message);
                                      if((reply?.status || 500) < 400){
                                        setBankSummary(undefined);
                                        setBankADD(undefined);
                                        setBankREMOVE(undefined);
                                        setBankMessage(undefined);
                                        setBankKeyId(undefined);
                                        setSelectedFile(undefined);
                                        setAlertLevel({severity:"success"});
                                        setAlertTitle("Saved");
                                        setAlertMessage("Changes saved successfully!");
                                      } else {
                                        setAlertLevel({severity:"error"});
                                        setAlertTitle("Issue saving changes!");
                                        setAlertMessage(`${reply?.data?.message}\n\nPlease reach out to CONA if issue persists.`);
                                      }
                                      setOpenAlertWindow(true);
                                    })
                                  }
                                }
                              }
                            ><Save/> Apply Changes
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  ):((bankMessage && bankErrorBody)?(
                    <Grid item xs={'auto'} md={'auto'} xl={'auto'}>
                      <Card className={classes.card}>
                        <CardContent>
                          <Typography variant="h5" className={classes.paragraph}>
                            Could not process file
                          </Typography>
                          <Alert severity="error">
                            <AlertTitle>Error</AlertTitle>   
                            {bankMessage} 
                          </Alert>
                          {(bankErrorBody.bank_with_issue)?(<Alert severity="warning"><AlertTitle>Bank(s) With Issue</AlertTitle>{Object.entries(bankErrorBody.bank_with_issue).map((bank: any) => bank[1].bankID).join(', ')}</Alert>) : (<div></div>)}
                          {(bankErrorBody.duplicateCustomerNums)?(<Alert severity="warning"><AlertTitle>Duplicate Customer(s)</AlertTitle>{(bankErrorBody.duplicateCustomerNums).join(', ')}</Alert>) : (<div></div>)}
                        </CardContent>
                      </Card>
                    </Grid>
                ):(<div></div>))}
        
      {
          (!bankErrorBody && bankADD && bankREMOVE)?( /* Upload Detected */ 
            <Grid item xs={'auto'} md={'auto'} xl={'auto'}>
              <Card className={classes.card}>
                  <TableAbstraction
                  settingsKey="ADM_BNK"
                  options={{
                    draggableColumns: {
                      enabled: true,
                    },
                    serverSide: false,
                    selectableRows: "none",
                  }}
                  title="Modifications"
                  columns={[ // salesOffice:string, bankID: string, bankName: string, customerNum:string, change:string
                    {
                      label: "Sales Office",
                      name: "salesOffice",
                      options: {
                        filter: true,
                        customBodyRender: (value: any) => `${value} - ${determineConfig(codes, "SALESOFFICES", value)}` ,
                      },
                    },
                    {
                      label: "Bank ID",
                      name: "bankID",
                      options: {
                        searchable: true,
                        filter: true,
                      },
                    },
                    {
                      label: "Bank Name",
                      name: "bankName",
                      options: {
                        searchable: true,
                        filter: true,
                      },
                    },
                    {
                      label: "Customer Number",
                      name: "customerNum",
                      options: {
                        searchable: true,
                        filter: false,
                      },
                    },
                    {
                      label: "Change",
                      name: "change",
                      options: {
                        searchable: true,
                        filter: true,
                        customBodyRender: (value: any) => { return (
                                <Tooltip title={value}>
                                    {(value === 'ADD')?(<AddCircle  style={{color: "Green" }}/>):<RemoveCircle  style={{color: "Crimson" }}/>}
                                </Tooltip>
                          )},
                      },
                    },
                  ]}
                  data={bankADD.concat(bankREMOVE)}
                />
              </Card>
            </Grid>
          ):(/* No Changes To Show */<div></div>)
      }
      </Grid>
      <Modal
        open={openAlertWindow}
        onClose={handleModalClose}
      >
        <div style={modalStyle} className={classes.paper} >
                <Alert severity={alertLevel.severity}>
                    <AlertTitle>{alertTitle}</AlertTitle>
                    {alertMessage}
                    <div style={{ paddingRight: "5px", textAlign:"right"}}>
                      <React.Fragment>
                        <Tooltip title={"Close"}>
                            <IconButton onClick={handleModalClose}>
                              <Clear />
                            </IconButton>
                        </Tooltip>
                      </React.Fragment>
                    </div>
                </Alert>
        </div>
      </Modal>
    </div>
    ) : (
          <Alert severity="error">
            <AlertTitle><Lock/> Access Error</AlertTitle>   
            You do not currently have access to <strong>Bulk Admin</strong> activities for the <strong>{salesOrg}</strong> bottler. 
          </Alert>
        )
  );
}